/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Column } from '../../layout/flexbox';
import ikonLogoBlue from '../../assets/images/ikonLogoGold.svg';
import background from '../../assets/images/backgroundDark.png';
import { useSelector } from 'react-redux';
import { IAppState } from '../../core/redux/IAppState';
import { LOGIN_ROUTE, ROOT_PATH } from '../../core/routes';
import { FADE_IN_ANIMATION } from '../../styles/animations';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Auth0Login } from './Auth0Login';
import { useAuthBootstrap } from './useAuthBootstrap';

const Root = styled('div')`
  ${FADE_IN_ANIMATION};
  background: url("${background}") no-repeat center center fixed;
  height: 100%;
  background-size: cover;
`;

const LogoTop = styled('img')`
  margin-right: auto;
  position: fixed;
  height: 80px;
  padding: 15px;
  @media (max-height: 715px) {
    display: none;
  }
`;

export const LoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    error: authError,
    loginWithRedirect,
  } = useAuth0();

  const { selectedResort, userState } = useAuthBootstrap();

  const resources = useSelector((state: IAppState) => state.locale);
  const isConfigLoaded = useSelector((state: IAppState) => !!state.resortToolsConfig?.isConfigLoaded);

  const fromPath = (location.state as any)?.from?.pathname ?? ROOT_PATH;
  const fromSearch = (location.state as any)?.from?.search ?? '';

  // Ready when config has been loaded (default or from DB)
  const hasUserProfile = !!userState?.firstName;
  const resortReady = !!selectedResort;

  const isReady =
    !isAuthLoading &&
    !!isAuthenticated &&
    hasUserProfile &&
    resortReady &&
    !!isConfigLoaded;

  /**
   * Show the loader while:
   *  - Auth0 is initializing, OR
   *  - User is authenticated but app is still bootstrapping (profile/resort/config)
   * Do NOT show the loader when unauthenticated & Auth0 idle → show login button.
   */
  const showLoader =
    isAuthLoading ||
    (!!isAuthenticated && (!hasUserProfile || !resortReady || !isConfigLoaded));

  /**
   * Prevent the "blank frame" between isReady=true and navigation:
   */
  const [navPending, setNavPending] = React.useState(false);

  React.useEffect(() => {
    if (isReady && !navPending) {
      setNavPending(true); 
      const target = fromPath !== LOGIN_ROUTE ? `${fromPath}${fromSearch}` : ROOT_PATH;
      // Allow one paint with loader, then navigate and unmount this page.
      requestAnimationFrame(() => {
        navigate(target, { replace: true });
      });
    }
  }, [isReady, navPending, fromPath, fromSearch, navigate]);

  // - Don’t show loader if there’s a hard auth error
  // - Show loader while core conditions are loading or once nav has started
  const isLoading = !authError && (showLoader || navPending);

  return (
    <Root>
      <LogoTop src={ikonLogoBlue} alt="IKON logo" />
      <Column rootCss={css`height: 100%;`}>
        <Column alignItems="center" justifyContent="center" flexGrow={1}>
          <Auth0Login
            isLoading={isLoading}
            authError={authError as Error | undefined}
            resources={resources}
            fromPath={fromPath}
            onLogin={(path) => {
              loginWithRedirect({ appState: { returnTo: path } });
            }}
          />
        </Column>
      </Column>
    </Root>
  );
};
